// Copyright 2026 FlairCV Inc.
// SPDX-License-Identifier: LicenseRef-FlairCV-Enterprise

import { useEffect, useState } from 'react';
import { useCaptchaToken } from '../components/recaptcha-provider';
import { useResendLoginCode, useVerifyLoginCode } from './useLoginCode';
import { isChallengeDead, type LoginChallenge } from '../types/login-outcome';
import { loginCodeSchema } from '../types/schemas';

/**
 * Mirrors the server's per-challenge resend cooldown. The server enforces it;
 * this only keeps the button from inviting a request that will be refused.
 */
export const LOGIN_CODE_RESEND_COOLDOWN_SECONDS = 60;

const CODE_LENGTH = 6;

/** State and handlers behind the code-entry step of the login page. */
export function useLoginCodeStep(challenge: LoginChallenge) {
  const [code, setCodeState] = useState('');
  const [fieldError, setFieldError] = useState<string | undefined>();
  const [resendCooldown, setResendCooldown] = useState(LOGIN_CODE_RESEND_COOLDOWN_SECONDS);
  const verify = useVerifyLoginCode();
  const resend = useResendLoginCode();
  const getCaptchaToken = useCaptchaToken();

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = window.setInterval(() => {
      setResendCooldown((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [resendCooldown]);

  /** Accepts digits only, so a pasted "123 456" still lands as 123456. */
  function setCode(raw: string) {
    setCodeState(raw.replace(/\D/g, '').slice(0, CODE_LENGTH));
    if (fieldError) setFieldError(undefined);
    if (verify.error) verify.reset();
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setFieldError(undefined);

    const result = loginCodeSchema.safeParse({ code });
    if (!result.success) {
      setFieldError(result.error.issues[0]?.message);
      return;
    }

    const captchaToken = await getCaptchaToken('verify_login_code');
    verify.mutate({
      challengeToken: challenge.challengeToken,
      code: result.data.code,
      password: challenge.password,
      captchaToken,
    });
  }

  async function resendCode() {
    if (resendCooldown > 0 || resend.isPending) return;
    verify.reset();
    const captchaToken = await getCaptchaToken('resend_login_code');
    resend.mutate(
      { challengeToken: challenge.challengeToken, captchaToken },
      {
        onSuccess: () => {
          setCodeState('');
          setResendCooldown(LOGIN_CODE_RESEND_COOLDOWN_SECONDS);
        },
      },
    );
  }

  // Once the server has given up on this challenge there is nothing left to
  // type; the only way forward is a fresh login.
  const challengeDead = isChallengeDead(verify.error) || isChallengeDead(resend.error);
  const errorMessage = verify.error?.message ?? resend.error?.message;

  return {
    code,
    setCode,
    codeLength: CODE_LENGTH,
    fieldError,
    errorMessage,
    challengeDead,
    submit,
    isVerifying: verify.isPending,
    resendCode,
    resendCooldown,
    isResending: resend.isPending,
    resendMessage: resend.isSuccess ? resend.data : undefined,
  };
}
